import { clarity } from 'react-microsoft-clarity';

const normalizeClarityValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value) || typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const getClarityApi = () => {
  if (clarity) {
    return clarity;
  }
  if (typeof window !== 'undefined' && window.clarity) {
    return window.clarity;
  }
  return null;
};

const isClarityReady = () => {
  try {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      return true;
    }

    const api = getClarityApi();
    if (!api || typeof api.hasStarted !== 'function') {
      return false;
    }
    return !!api.hasStarted();
  } catch (e) {
    return false;
  }
};

const trackClarityEvent = (name, params = {}) => {
  try {
    const api = getClarityApi();
    if (!isClarityReady() || !api || typeof api.setTag !== 'function') return;

    api.setTag('event_name', name);

    Object.entries(params || {}).forEach(([key, value]) => {
      api.setTag(`${name}.${key}`, normalizeClarityValue(value));
    });
  } catch (e) {
    console.warn('trackClarityEvent failed', e);
  }
};

export const setUserProperties = (properties = {}) => {
  try {
    if (window && window.gtag) {
      window.gtag('set', 'user_properties', properties || {});
    }

    const api = getClarityApi();
    if (!isClarityReady() || !api || typeof api.setTag !== 'function') return;

    Object.entries(properties || {}).forEach(([key, value]) => {
      api.setTag(key, normalizeClarityValue(value));
    });
  } catch (e) {
    console.warn('setUserProperties failed', e);
  }
};

export const identifyUser = (userId) => {
  const safeUserId = userId == null ? null : String(userId);

  try {
    if (window && window.gtag) {
      // Set GA4 built-in user_id (for cross-device stitching)
      window.gtag('set', { user_id: safeUserId });
      // Also set a lightweight user property for easy filtering
      window.gtag('set', 'user_properties', { logged_in: 'true', internal_user_id: safeUserId });
    }

    const api = getClarityApi();
    if (isClarityReady() && api && typeof api.identify === 'function') {
      api.identify(safeUserId, { logged_in: 'true', internal_user_id: safeUserId });
    }
  } catch (e) {
    // swallow errors to avoid breaking app flow
    console.warn('identifyUser failed', e);
  }
};

export const clearIdentity = () => {
  try {
    if (window && window.gtag) {
      window.gtag('set', { user_id: null });
      window.gtag('set', 'user_properties', { logged_in: 'false' });
    }

    const api = getClarityApi();
    if (isClarityReady() && api && typeof api.setTag === 'function') {
      api.setTag('logged_in', 'false');
      api.setTag('internal_user_id', '');
    }
  } catch (e) {
    console.warn('clearIdentity failed', e);
  }
};

export const trackEvent = (name, params = {}) => {
  try {
    if (window && window.gtag) {
      window.gtag('event', name, params || {});
    }

    trackClarityEvent(name, params);
  } catch (e) {
    console.warn('trackEvent failed', e);
  }
};

const analytics = {
  identifyUser,
  clearIdentity,
  trackEvent,
  setUserProperties,
};

export default analytics;
